import { api, unwrap } from "@lumberset/api-client";
import { date, money } from "@lumberset/takeoff-ui";
import {
  Button,
  Container,
  Drawer,
  EmptyState,
  Field,
  Input,
  RadioGroup,
  SectionHeader,
  Skeleton,
  Table,
  Tabs,
  toast,
} from "@lumberset/ui";
import { ArchiveBox, ArrowUpRightOnBox, Buildings, MagnifyingGlass, Plus } from "@medusajs/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useDeferredValue, useState } from "react";
import { Crumbs } from "../../../components/crumbs";
import { SignInAsPrompt } from "../../../components/sign-in-as";
import { SubscriptionStatus } from "../../../components/status";
import { dollarsToCents, wholeNumber } from "../../../lib/money";
import { archivedYardsQuery, billingPlansQuery, workspacesSearchQuery } from "../../../lib/queries";

export const Route = createFileRoute("/_app/yards/")({
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(workspacesSearchQuery("")),
  component: Yards,
});

type View = "current" | "archived";

/** Who Open as owner signs in as, and in which workspace. */
type Opening = { userId: string; name: string; workspace: { id: string; name: string } };

function Yards() {
  const navigate = useNavigate();
  const { user: me } = Route.useRouteContext();
  const [view, setView] = useState<View>("current");
  const [text, setText] = useState("");
  const q = useDeferredValue(text.trim());
  // Searched on the server: by the workspace's name, or the name or email of someone in it.
  const { data, isPending } = useQuery(workspacesSearchQuery(q));
  const yards = data?.yards ?? [];
  // The archived ones, loaded when their tab is opened, and searched here by name.
  const archived = useQuery({ ...archivedYardsQuery, enabled: view === "archived" });
  const archivedYards = (archived.data?.yards ?? []).filter(
    (yard) => !q || yard.name.toLowerCase().includes(q.toLowerCase()),
  );
  const [opening, setOpening] = useState<Opening | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <Crumbs trail={[{ label: "Workspaces" }]} />
      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title="Workspaces"
          description="Every lumber yard and builder on LumberSet, newest first. Open one to see its people, projects and billing, or to pause, archive, merge, close or delete it."
          actions={
            <Button size="small" onClick={() => setCreating(true)}>
              <Plus /> New workspace for a client
            </Button>
          }
        />
        <div className="px-6 py-3">
          <Tabs value={view} onValueChange={(value) => setView(value as View)}>
            <Tabs.List>
              <Tabs.Trigger value="current">Current</Tabs.Trigger>
              <Tabs.Trigger value="archived">Archived</Tabs.Trigger>
            </Tabs.List>
          </Tabs>
        </div>
        <div className="px-6 py-3 sm:max-w-sm">
          <Input
            type="search"
            placeholder="Name, member or email"
            aria-label="Search by workspace name, or by the name or email of someone in it"
            prefix={<MagnifyingGlass />}
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
        </div>

        {view === "archived" ? (
          archived.isPending ? (
            <div className="flex flex-col gap-y-3 px-6 py-4">
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-5 w-1/2" />
            </div>
          ) : archivedYards.length === 0 ? (
            <EmptyState
              icon={<ArchiveBox />}
              title={q ? "Nothing matches" : "Nothing archived"}
              description={
                q
                  ? "Try a different name."
                  : "Workspaces you archive or merge wait here, as they were, out of every other list."
              }
            />
          ) : (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Workspace</Table.HeaderCell>
                  <Table.HeaderCell>Archived</Table.HeaderCell>
                  <Table.HeaderCell>Why</Table.HeaderCell>
                  <Table.HeaderCell className="text-right">Billable Quotes</Table.HeaderCell>
                  <Table.HeaderCell className="text-right">Billed</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {archivedYards.map((yard) => (
                  <Table.Row
                    key={yard.id}
                    data-clickable="true"
                    onClick={() => navigate({ to: "/yards/$id", params: { id: yard.id } })}
                  >
                    <Table.Cell>
                      <Link
                        to="/yards/$id"
                        params={{ id: yard.id }}
                        className="txt-compact-small-plus text-ui-fg-base outline-none hover:underline focus-visible:underline"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {yard.name}
                      </Link>
                      <div className="txt-compact-xsmall text-ui-fg-muted">
                        {yard.kind === "builder" ? "Builder" : "Lumber yard"}, since{" "}
                        {date(yard.createdAt)}
                      </div>
                    </Table.Cell>
                    <Table.Cell className="whitespace-nowrap">
                      {yard.archivedAt ? date(yard.archivedAt) : "-"}
                      {yard.archivedByName ? (
                        <div className="txt-compact-xsmall text-ui-fg-muted">
                          by {yard.archivedByName}
                        </div>
                      ) : null}
                    </Table.Cell>
                    <Table.Cell className="min-w-[200px]">
                      {yard.mergedIntoId ? (
                        <span>
                          Merged into{" "}
                          <Link
                            to="/yards/$id"
                            params={{ id: yard.mergedIntoId }}
                            className="text-ui-fg-interactive hover:underline"
                            onClick={(event) => event.stopPropagation()}
                          >
                            {yard.mergedIntoName ?? "another workspace"}
                          </Link>
                        </span>
                      ) : (
                        (yard.archiveNote ?? (
                          <span className="text-ui-fg-muted">No reason given</span>
                        ))
                      )}
                    </Table.Cell>
                    <Table.Cell className="tabular text-right">{yard.billableQuotes}</Table.Cell>
                    <Table.Cell className="tabular whitespace-nowrap text-right text-ui-fg-base">
                      {money(yard.billedCents)}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )
        ) : isPending ? (
          <div className="flex flex-col gap-y-3 px-6 py-4">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-5 w-1/2" />
          </div>
        ) : yards.length === 0 ? (
          <EmptyState
            icon={<Buildings />}
            title={q ? "Nothing matches" : "No workspaces yet"}
            description={
              q
                ? "Try another name, or the name or email of someone in the workspace."
                : "A workspace appears here as soon as someone signs up."
            }
          />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Workspace</Table.HeaderCell>
                <Table.HeaderCell>Billing Plan</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Trial takeoffs left</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Takeoff credits</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Account credit</Table.HeaderCell>
                <Table.HeaderCell>Card on file</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Billable Quotes</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Billed</Table.HeaderCell>
                <Table.HeaderCell>
                  <span className="sr-only">Open as owner</span>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {yards.map((yard) => (
                <Table.Row
                  key={yard.id}
                  data-clickable="true"
                  onClick={() => navigate({ to: "/yards/$id", params: { id: yard.id } })}
                >
                  <Table.Cell>
                    {/* A real link too, so the page opens from the keyboard or in a new tab. */}
                    <Link
                      to="/yards/$id"
                      params={{ id: yard.id }}
                      className="txt-compact-small-plus text-ui-fg-base outline-none hover:underline focus-visible:underline"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {yard.name}
                    </Link>
                    <div className="txt-compact-xsmall text-ui-fg-muted">
                      {yard.kind === "builder" ? "Builder" : "Lumber yard"}, since{" "}
                      {date(yard.createdAt)}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    {yard.billingPlanName ?? "-"}
                    {yard.quoteFeeOverrideCents !== null ? (
                      <div className="txt-compact-xsmall text-ui-fg-muted">
                        Agreed Quote Fee {money(yard.quoteFeeOverrideCents)}
                      </div>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <SubscriptionStatus status={yard.status} />
                  </Table.Cell>
                  <Table.Cell className="tabular text-right">
                    {yard.trialQuotesRemaining ?? "-"}
                  </Table.Cell>
                  <Table.Cell className="tabular whitespace-nowrap text-right">
                    {yard.creditQuotes ?? "-"}
                    {yard.discount ? (
                      <div className="txt-compact-xsmall text-ui-fg-muted">
                        {yard.discount.code}
                      </div>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell className="tabular whitespace-nowrap text-right">
                    {yard.accountCreditCents === null ? "-" : money(yard.accountCreditCents)}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    {yard.hasPaymentMethod === null ? "-" : yard.hasPaymentMethod ? "Yes" : "No"}
                  </Table.Cell>
                  <Table.Cell className="tabular text-right">{yard.billableQuotes}</Table.Cell>
                  <Table.Cell className="tabular whitespace-nowrap text-right text-ui-fg-base">
                    {money(yard.billedCents)}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap text-right">
                    {yard.owner ? (
                      <Button
                        size="small"
                        variant="secondary"
                        disabled={yard.owner.signInRefusal !== null}
                        title={
                          yard.owner.signInRefusal ??
                          `Signs in as ${yard.owner.name}, its Owner, in the Yard app`
                        }
                        onClick={(event) => {
                          event.stopPropagation();
                          const owner = yard.owner;
                          if (!owner) return;
                          setOpening({
                            userId: owner.id,
                            name: owner.name,
                            workspace: { id: yard.id, name: yard.name },
                          });
                        }}
                      >
                        <ArrowUpRightOnBox /> Open as owner
                      </Button>
                    ) : (
                      <span className="txt-compact-xsmall text-ui-fg-muted">No Owner</span>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Container>

      <SignInAsPrompt
        person={opening}
        surface="app"
        workspaces={opening ? [opening.workspace] : []}
        adminName={me.name}
        onClose={() => setOpening(null)}
      />

      <Drawer open={creating} onOpenChange={setCreating}>
        <Drawer.Content>
          {creating ? <ClientWorkspaceForm onClose={() => setCreating(false)} /> : null}
        </Drawer.Content>
      </Drawer>
    </>
  );
}

/**
 * Sets up a workspace for a client and lets its Owner in by email, with the
 * trial takeoffs and account credit it starts with. Works while sign-up is off.
 */
function ClientWorkspaceForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: plans } = useQuery(billingPlansQuery);
  const standard = plans?.billingPlans.find((plan) => plan.isDefault);
  const [kind, setKind] = useState<"yard" | "builder">("yard");

  const create = useMutation({
    mutationFn: (form: FormData) => {
      const text = (name: string) => String(form.get(name) ?? "").trim();
      return unwrap(
        api.admin.workspaces.$post({
          json: {
            name: text("name"),
            kind,
            ownerName: text("ownerName"),
            ownerEmail: text("ownerEmail"),
            trialTakeoffs: Math.min(
              1000,
              wholeNumber(form.get("trialTakeoffs"), standard?.trialQuotes ?? 0),
            ),
            accountCreditCents: dollarsToCents(form.get("accountCredit")) ?? 0,
            note: text("note") || undefined,
          },
        }),
      );
    },
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin", "yards"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "overview"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "ledger"] }),
      ]);
      const { owner, workspace } = result;
      if (!owner.delivered) {
        toast.warning(
          `${workspace.name} is set up, but the email to ${owner.email} did not go out`,
          {
            description: "Open the workspace and send it again from its members.",
          },
        );
      } else if (owner.sent === "welcome") {
        toast.success(`${workspace.name} is set up`, {
          description: `${owner.email} has an email with a link to choose a password, good for 7 days. They are its Owner.`,
        });
      } else {
        toast.success(`${workspace.name} is set up`, {
          description: `${owner.email} already has a LumberSet account, so they have an invitation to accept as its Owner.`,
        });
      }
      onClose();
      await navigate({ to: "/yards/$id", params: { id: workspace.id } });
    },
    onError: (error) => toast.error(error.message),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    create.mutate(new FormData(event.currentTarget));
  }

  return (
    <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
      <Drawer.Header>
        <Drawer.Title>New workspace for a client</Drawer.Title>
        <Drawer.Description>
          Sets the workspace up and emails its Owner. Someone new gets a link to choose a password
          and is the Owner at once; someone who already signs in to LumberSet gets an invitation to
          accept. Works while sign-up is off.
        </Drawer.Description>
      </Drawer.Header>

      <Drawer.Body className="flex flex-col gap-y-5">
        <Field label="Workspace name" hint="What their customers or suppliers see.">
          {(control) => (
            <Input
              {...control}
              name="name"
              required
              maxLength={120}
              autoComplete="off"
              autoFocus
              placeholder="Big Timber Supply"
            />
          )}
        </Field>

        <RadioGroup
          value={kind}
          aria-label="Kind of workspace"
          onValueChange={(value) => setKind(value as "yard" | "builder")}
          className="sm:grid-cols-2"
        >
          <RadioGroup.ChoiceBox
            value="yard"
            label="Lumber yard"
            description="Sells lumber and quotes its customers."
          />
          <RadioGroup.ChoiceBox
            value="builder"
            label="Builder"
            description="Buys lumber: takeoffs, budgets and quotes from yards."
          />
        </RadioGroup>

        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Owner's name">
            {(control) => (
              <Input
                {...control}
                name="ownerName"
                required
                maxLength={120}
                autoComplete="off"
                placeholder="Pat Murray"
              />
            )}
          </Field>
          <Field label="Owner's email">
            {(control) => (
              <Input
                {...control}
                name="ownerEmail"
                type="email"
                required
                maxLength={254}
                autoComplete="off"
                placeholder="pat@bigtimber.com"
              />
            )}
          </Field>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Trial takeoffs" hint="Free LumberSet takeoffs, used before anything else.">
            {(control) => (
              <Input
                {...control}
                key={standard?.id ?? "loading"}
                name="trialTakeoffs"
                type="number"
                inputMode="numeric"
                min={0}
                max={1000}
                step={1}
                required
                defaultValue={standard?.trialQuotes ?? 1}
              />
            )}
          </Field>
          <Field
            label="Account credit"
            optional
            hint="Platform credit in dollars: pays its LumberSet takeoff and change order fees before any card."
          >
            {(control) => (
              <Input
                {...control}
                name="accountCredit"
                inputMode="decimal"
                prefix="$"
                placeholder="300.00"
                pattern="[0-9,]*\.?[0-9]{0,2}"
              />
            )}
          </Field>
        </div>

        <Field label="Reason" optional hint="Goes on the Ledger with the account credit.">
          {(control) => (
            <Input
              {...control}
              name="note"
              maxLength={300}
              autoComplete="off"
              placeholder="Testing LumberSet on their own jobs"
            />
          )}
        </Field>
      </Drawer.Body>

      <Drawer.Footer>
        <Button type="button" variant="secondary" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="small" isLoading={create.isPending}>
          Set up and email the Owner
        </Button>
      </Drawer.Footer>
    </form>
  );
}
